import { Injectable, ComponentRef, ApplicationRef, ComponentFactoryResolver, Injector, Type } from '@angular/core';

import { getUuid } from '@common/util/util';

import { SystemNotificationComponent } from '../component/system-notification/system-notification.component';
import { SystemNotificationConfig, SystemNotificationRef } from '../types/system-notification.interface';

@Injectable({
  providedIn: 'root',
})
export class SystemNotificationService {
  private overlayComponentRef: ComponentRef<SystemNotificationComponent> | null = null;
  private currentOverlayRef: SystemNotificationRef | null = null;

  constructor(
    private appRef: ApplicationRef,
    private componentFactoryResolver: ComponentFactoryResolver,
    private injector: Injector,
  ) {}

  show(config: SystemNotificationConfig = {}): SystemNotificationRef {
    this.hide();

    const componentFactory = this.componentFactoryResolver.resolveComponentFactory(SystemNotificationComponent);
    const componentRef = componentFactory.create(this.injector);

    const overlayRef: SystemNotificationRef = {
      close: () => this.hide(),
      updateConfig: (newConfig: Partial<SystemNotificationConfig>) => this.updateConfig(newConfig),
    };

    componentRef.instance.config = { ...config };
    componentRef.instance.overlayRef = overlayRef;

    this.appRef.attachView(componentRef.hostView);
    document.body.appendChild(componentRef.location.nativeElement);

    this.overlayComponentRef = componentRef;
    this.currentOverlayRef = overlayRef;

    return overlayRef;
  }

  hide(): void {
    if (this.overlayComponentRef) {
      this.appRef.detachView(this.overlayComponentRef.hostView);
      this.overlayComponentRef.destroy();
      this.overlayComponentRef = null;
      this.currentOverlayRef = null;
    }
  }

  private updateConfig(newConfig: Partial<SystemNotificationConfig>): void {
    if (this.overlayComponentRef) {
      this.overlayComponentRef.instance.config = {
        ...this.overlayComponentRef.instance.config,
        ...newConfig,
      };
    }
  }

  showSpinner(message: string = 'Loading...'): SystemNotificationRef {
    return this.show({
      showSpinner: true,
      message,
    });
  }

  showMessage(message: string, icon?: string): SystemNotificationRef {
    return this.show({
      message,
      icon,
    });
  }

  showWithAction(
    message: string,
    actionLabel: string,
    actionCallback: () => void,
    icon?: string,
  ): SystemNotificationRef {
    return this.show({
      message,
      icon,
      actions: [
        {
          id: getUuid(),
          label: actionLabel,
          action: actionCallback,
        },
      ],
    });
  }

  showComponent(component: Type<unknown>, data?: unknown): SystemNotificationRef {
    return this.show({
      component,
      componentData: data,
    });
  }

  isVisible(): boolean {
    return this.overlayComponentRef !== null;
  }
}
